import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Clock, 
  ArrowLeft, 
  CheckCircle, 
  Building2, 
  Target,
  Zap,
  Shield,
  TrendingUp,
  Share2,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TestimonialDetail = ({ testimonial }) => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${testimonial.clientCompany} - Envael Success Story`,
        text: testimonial.testimonial,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <div className="mb-8">
        <Link to="/testimonials">
          <Button variant="ghost" className="group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Testimonials
          </Button>
        </Link>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <img
                src={testimonial.clientAvatar}
                alt={testimonial.clientName}
                className="w-20 h-20 rounded-full object-cover border-4 border-primary/20"
              />
              {testimonial.verified && (
                <CheckCircle className="absolute -bottom-1 -right-1 h-6 w-6 text-green-500 bg-white rounded-full" />
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {testimonial.clientName}
              </h1>
              <p className="text-lg text-primary font-medium mb-1">
                {testimonial.clientTitle}
              </p>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span>{testimonial.clientCompany}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {renderStars(testimonial.rating)}
            <span className="text-sm text-muted-foreground ml-2">
              ({testimonial.rating}/5)
            </span>
          </div>
        </div>

        {/* Project Info */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {testimonial.projectType}
          </Badge>
          <Badge variant="outline" className="text-sm px-3 py-1">
            {testimonial.clientIndustry}
          </Badge>
          {testimonial.featured && (
            <Badge className="text-sm px-3 py-1 bg-primary text-primary-foreground">
              Featured Success Story
            </Badge>
          )}
        </div>

        {/* Location and Date */}
        <div className="flex items-center gap-6 text-muted-foreground mb-6">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>{testimonial.clientLocation}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{new Date(testimonial.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>

        {/* Share Button */}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleShare}>
            <Share2 className="mr-2 h-4 w-4" />
            Share Story
          </Button>
        </div>
      </div>

      {/* Main Testimonial */}
      <Card className="mb-8">
        <CardContent className="p-8">
          <blockquote className="text-xl text-muted-foreground leading-relaxed mb-6">
            "{testimonial.fullTestimonial}"
          </blockquote>
          
          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-border">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Project Duration</p>
                <p className="font-semibold">{testimonial.projectDuration}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Project Value</p>
                <p className="font-semibold">{testimonial.projectValue}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="font-semibold">{testimonial.category}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Section */}
      <Card className="mb-8">
        <CardContent className="p-8">
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-primary" />
            Key Results & Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {testimonial.results.map((result, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-foreground">{result}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Challenges & Solutions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <Card>
          <CardContent className="p-8">
            <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-500" />
              Challenges Faced
            </h3>
            <div className="space-y-3">
              {testimonial.challenges.map((challenge, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2"></div>
                  <span className="text-muted-foreground">{challenge}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-8">
            <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Our Solutions
            </h3>
            <div className="space-y-3">
              {testimonial.solutions.map((solution, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                  <span className="text-muted-foreground">{solution}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tags */}
      <Card className="mb-8">
        <CardContent className="p-8">
          <h3 className="text-xl font-bold text-foreground mb-4">Technologies & Services</h3>
          <div className="flex flex-wrap gap-2">
            {testimonial.tags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-sm px-3 py-1">
                {tag}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">
            Ready to Create Your Success Story?
          </h3>
          <p className="text-primary-foreground/90 mb-6 max-w-2xl mx-auto">
            Join {testimonial.clientCompany} and other successful businesses that have transformed their operations with Envael's innovative solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg" asChild>
              <Link to="/contact">
                Start Your Project
                <ExternalLink className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10" asChild>
              <Link to="/services">
                View Our Services
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestimonialDetail;
